// Mock data models for the PAR Intelligence drawer (demo only — no backend).
// Mirrors the lightweight model-file style of insightCardModels.ts.

import {IColumnMetadata} from "@/features/application/api/applicationModels";
import {sortingDirections} from "@/util/sortUtil";
import {BentoPersonaId} from "../../bentoPersona";
import {COACHES} from "../../coachCatalog";
import type {AgentIdentity} from "../../insightAgents";
import type {InsightCard} from "../../insightCardModels";

export type IntelligenceView =
    | "chat"
    | "workflows"
    | "history"
    | "saved"
    | "agents"
    | "memory";

/** A single surfaced insight, paired with the agent that found it, for the empty-state teaser. */
export interface InsightTeaser {
    card: InsightCard;
    agent?: AgentIdentity;
}

export type MessageRole = "user" | "assistant";

export type MetricDirection = "up" | "down" | "flat";

export interface MessageMetric {
    label: string;
    value: string;
    direction: MetricDirection;
}

export type MessageActionKind =
    | "campaign"
    | "automation"
    | "settings"
    | "report"
    | "alert"
    | "schedule"
    | "comms";

export interface MessageAction {
    id: string;
    kind: MessageActionKind;
    category: string;
    title: string;
    description: string;
    ctaLabel: string;
    /** Assistant confirmation appended to the chat once the action is run. */
    result: string;
}

export interface DiagnosisKpi {
    label: string;
    value: string;
    context?: string;
    /** Relative to business goal: positive = good, negative = bad, neutral = informational. */
    sentiment?: "positive" | "negative" | "neutral";
}

export interface DiagnosisReport {
    title: string;
    key_finding: string;
    time_period: string;
    what_happened: string;
    why_it_happened: string;
    why_it_matters: string;
    follow_up_with: string;
    kpis: DiagnosisKpi[];
    chart: string | null;
}

export interface BriefItem {
    metric: string;
    scope: string;
    value: string;
    movement: string;
    /** Relative to business goal: positive = good, attention = needs a look, neutral = informational. */
    severity?: "positive" | "neutral" | "attention";
    note?: string;
}

export interface MorningBrief {
    title: string;
    period: string;
    headline: string;
    highlights: BriefItem[];
    watch_list: BriefItem[];
    chart: string | null;
}

export interface ConversationMessage {
    id: string;
    role: MessageRole;
    text: string;
    timestamp: string;
    /** Assistant-only: short source/confidence tags shown under the bubble. */
    sources?: string[];
    /** Assistant-only: compact metric callouts. */
    metrics?: MessageMetric[];
    /** Assistant-only: suggested follow-up prompts. */
    followUps?: string[];
    /** Assistant-only: agentic actions the user can take from the response. */
    actions?: MessageAction[];
    /** Assistant-only: intermediate progress steps shown while the agent works. */
    activity?: string[];
    /** True once the text stream is complete; collapses activity steps into an expander. */
    isComplete?: boolean;
    /** Assistant-only: structured root-cause report from the ANOMALY investigation path. */
    diagnosisReport?: DiagnosisReport;
    /** Assistant-only: structured daily digest from the BRIEFING investigation path. */
    morningBrief?: MorningBrief;
    /** Assistant-only: raw data rows backing a table/chart, keyed like the API response. */
    dataPoints?: Record<string, unknown>;
    /** Assistant-only: column definitions for dataPoints. */
    columnsMetadata?: IColumnMetadata[];
    sortColumn?: string;
    sortDirection?: sortingDirections;
    /** Assistant-only: Highcharts config (JSON string) to render alongside the table. */
    highchart?: string | null;
    /** Assistant-only: suggested filename when exporting dataPoints. */
    fileName?: string | null;
}

export type IntelligenceIcon =
    | "sparkle"
    | "chart"
    | "people"
    | "money"
    | "clock"
    | "bot"
    | "bulb"
    | "gift"
    | "alert"
    | "calendar"
    | "brain";

export interface SuggestedPrompt {
    id: string;
    icon: IntelligenceIcon;
    category: string;
    title: string;
    prompt: string;
    /** Personas this card is shown to. Omit to show to everyone. */
    personaIds?: BentoPersonaId[];
    /** coach:product keys this card depends on — shown when any is enabled. Omit for product-agnostic cards. */
    requiredProductIds?: string[];
}

export interface SavedPrompt {
    id: string;
    icon: IntelligenceIcon;
    category: string;
    title: string;
    prompt: string;
}

export type HistoryGroup = "Today" | "Yesterday" | "Last 7 days";

export interface PromptHistoryItem {
    id: string;
    title: string;
    preview: string;
    group: HistoryGroup;
    timestamp: string;
}

export type AgentStatus = "running" | "scheduled" | "paused";

export interface Agent {
    id: string;
    name: string;
    description: string;
    icon: IntelligenceIcon;
    status: AgentStatus;
    /** Human-readable cadence, e.g. "Daily at 6:00 AM". Undefined = on-demand. */
    schedule?: string;
    lastRun: string;
    nextRun?: string;
    runs: number;
    enabled: boolean;
}

export type MemoryCategory = "Preference" | "Context" | "Fact" | "Pinned";

export interface MemoryItem {
    id: string;
    category: MemoryCategory;
    text: string;
    source: string;
}

// ── Suggested prompts (empty-state cards) ───────────────────────────────────
// personaIds omitted = shown to all personas.
// requiredProductIds omitted = product-agnostic, always shown regardless of coach settings.
const ALL_PRODUCT_IDS = COACHES.flatMap((coach) =>
    coach.products.map((product) => `${coach.id}:${product.id}`)
);

export const suggestedPrompts: SuggestedPrompt[] = [
    // ── Universal ────────────────────────────────────────────────────────
    {
        id: "sg_brief",
        icon: "bulb",
        category: "Daily brief",
        title: "Brief me for today",
        prompt: "Give me a 5-bullet morning brief on what needs my attention today based on recent KPIs, anomalies, and trends.",
        // Needs at least one active coach to have anything to brief on.
        requiredProductIds: ALL_PRODUCT_IDS
    },

    // ── Above Store Operations ────────────────────────────────────────────
    {
        id: "sg_sales_drivers",
        icon: "chart",
        category: "Performance",
        title: "What drove sales yesterday?",
        prompt: "Break down what drove net sales across my locations yesterday and call out the biggest movers.",
        personaIds: ["area_ops", "store_manager", "super_user"],
        requiredProductIds: ["sell:ordering"]
    },
    {
        id: "sg_food_cost",
        icon: "money",
        category: "Food cost",
        title: "Find food cost leaks",
        prompt: "Identify the locations with the highest food cost variance this period and the likely drivers.",
        personaIds: ["area_ops", "store_manager", "super_user"],
        requiredProductIds: ["operate:inventory"]
    },
    {
        id: "sg_speed_above",
        icon: "clock",
        category: "Operations",
        title: "Where is speed of service slipping?",
        prompt: "Show the locations where drive-thru speed of service is trending worse than last month and why.",
        personaIds: ["area_ops", "super_user"],
        requiredProductIds: ["sell:driveThru"]
    },
    {
        id: "sg_labor_above",
        icon: "people",
        category: "Labor",
        title: "Which stores are over on labor?",
        prompt: "Rank my locations by labor cost percentage this week and flag any that are significantly above target.",
        personaIds: ["area_ops", "super_user"],
        requiredProductIds: ["operate:labor"]
    },

    // ── In-Store Operations ───────────────────────────────────────────────
    {
        id: "sg_speed_store",
        icon: "clock",
        category: "Operations",
        title: "How is my speed of service today?",
        prompt: "Show me drive-thru speed of service for my store today compared to last week and flag any hour where we slipped.",
        personaIds: ["store_manager", "super_user"],
        requiredProductIds: ["sell:driveThru"]
    },
    {
        id: "sg_labor_store",
        icon: "people",
        category: "Labor",
        title: "Optimize next week's labor",
        prompt: "Recommend a labor plan for next week at my store that protects speed of service without overstaffing.",
        personaIds: ["store_manager", "super_user"],
        requiredProductIds: ["operate:labor"]
    },
    {
        id: "sg_food_cost_store",
        icon: "money",
        category: "Food cost",
        title: "What's driving my food cost variance?",
        prompt: "Break down my store's food cost variance this period and identify the top contributing line items.",
        personaIds: ["store_manager", "area_ops", "super_user"],
        requiredProductIds: ["operate:inventory"]
    },
    {
        id: "sg_split_test",
        icon: "brain",
        category: "Platform Help",
        title: "How do I set up a split test?",
        prompt: "Guide me through setting up a split test in Punchh.",
        personaIds: ["marketing", "super_user"]
    },
    {
        id: "sg_punchh_campaigns",
        icon: "brain",
        category: "Platform Help",
        title: "Explain the different kinds of campaigns offered in Punchh.",
        prompt: "Explain the different kinds of campaigns available in Punchh and when to use each one.",
        personaIds: ["marketing", "super_user"]
    },
    // ── Marketing persona ────────────────────────────────────────────────
    {
        id: "sg_lapse_bucket",
        icon: "clock",
        category: "Loyalty",
        title: "Analyze lapse buckets",
        prompt: "Break down loyalty redemption rate by how long members had lapsed before being targeted — group by 0-30, 31-60, 61-90, and 90+ days — and tell me which bucket converts best.",
        personaIds: ["marketing", "super_user"],
        requiredProductIds: ["engage:loyalty"]
    },
    {
        id: "sg_redemption_rca",
        icon: "chart",
        category: "Loyalty",
        title: "Did loyalty redemption drop this month?",
        prompt: "Give me a root cause analysis into loyalty redemption in March versus February. What changed, which segments drove the delta, and what's the most likely explanation?",
        personaIds: ["marketing", "super_user"],
        requiredProductIds: ["engage:loyalty"]
    },
    {
        id: "sg_top_campaigns",
        icon: "gift",
        category: "Loyalty",
        title: "Which campaigns have the highest redemption rate?",
        prompt: "Rank all my loyalty campaigns by redemption rate and highlight the top performers. What do the best ones have in common?",
        personaIds: ["marketing", "super_user"],
        requiredProductIds: ["engage:campaign"]
    }
];

// ── Saved prompts ───────────────────────────────────────────────────────────
export const savedPromptsSeed: SavedPrompt[] = [
    {
        id: "sv_weekly_review",
        icon: "chart",
        category: "Performance",
        title: "Weekly performance review",
        prompt: "Summarize this week vs last week across sales, traffic, and average check by region."
    },
    {
        id: "sv_at_risk",
        icon: "alert",
        category: "Loyalty",
        title: "At-risk loyalty members",
        prompt: "List the segments of loyalty members most at risk of churning and suggested win-back offers."
    },
    {
        id: "sv_bottom5",
        icon: "people",
        category: "Operations",
        title: "Bottom 5 locations to coach",
        prompt: "Rank my bottom 5 locations this week and tell me the single highest-impact coaching action for each."
    }
];

// ── Prompt history ──────────────────────────────────────────────────────────
export const promptHistory: PromptHistoryItem[] = [
    {
        id: "h_today_1",
        title: "Drive-thru speed by daypart",
        preview: "Lunch is your slowest daypart at 3 locations…",
        group: "Today",
        timestamp: "9:12 AM"
    },
    {
        id: "h_today_2",
        title: "Loyalty enrollment recovery",
        preview: "Enrollment dipped 8% — here's a 3-step play…",
        group: "Today",
        timestamp: "8:03 AM"
    },
    {
        id: "h_yest_1",
        title: "Weekend sales recap",
        preview: "Saturday dinner outperformed forecast by 6%…",
        group: "Yesterday",
        timestamp: "4:47 PM"
    },
    {
        id: "h_yest_2",
        title: "Food cost variance — Midwest",
        preview: "Two stores account for 60% of the variance…",
        group: "Yesterday",
        timestamp: "11:20 AM"
    },
    {
        id: "h_week_1",
        title: "Labor plan for next week",
        preview: "Shift two associates from open to mid-day…",
        group: "Last 7 days",
        timestamp: "Mon"
    },
    {
        id: "h_week_2",
        title: "Top upsell opportunities",
        preview: "Combo attach rate is lagging at dinner…",
        group: "Last 7 days",
        timestamp: "Sun"
    }
];

// ── Agents (incl. scheduled) ────────────────────────────────────────────────
export const agentsSeed: Agent[] = [
    {
        id: "ag_morning_brief",
        name: "Morning Brief",
        description:
            "Compiles a 5-bullet brief of overnight performance and what needs attention.",
        icon: "bulb",
        status: "scheduled",
        schedule: "Daily at 6:00 AM",
        lastRun: "Today, 6:00 AM",
        nextRun: "Tomorrow, 6:00 AM",
        runs: 128,
        enabled: true
    },
    {
        id: "ag_loyalty_watch",
        name: "Loyalty Watchdog",
        description:
            "Monitors enrollment and redemption trends, flags anomalies, drafts recovery offers.",
        icon: "gift",
        status: "running",
        schedule: "Every 4 hours",
        lastRun: "Today, 8:00 AM",
        nextRun: "Today, 12:00 PM",
        runs: 642,
        enabled: true
    },
    {
        id: "ag_speed_sentinel",
        name: "Speed Sentinel",
        description:
            "Watches drive-thru speed of service and alerts when a location slips past target.",
        icon: "clock",
        status: "scheduled",
        schedule: "Weekdays at 11:00 AM & 5:00 PM",
        lastRun: "Yesterday, 5:00 PM",
        nextRun: "Today, 11:00 AM",
        runs: 89,
        enabled: true
    },
    {
        id: "ag_food_cost",
        name: "Food Cost Auditor",
        description:
            "Reconciles theoretical vs actual food cost and surfaces the biggest leaks weekly.",
        icon: "money",
        status: "scheduled",
        schedule: "Mondays at 7:00 AM",
        lastRun: "Mon, 7:00 AM",
        nextRun: "Next Mon, 7:00 AM",
        runs: 24,
        enabled: false
    },
    {
        id: "ag_review_responder",
        name: "Review Responder",
        description:
            "Drafts on-brand replies to new guest reviews for manager approval.",
        icon: "bot",
        status: "paused",
        lastRun: "3 days ago",
        runs: 311,
        enabled: false
    }
];

// ── Memory ──────────────────────────────────────────────────────────────────
export const memorySeed: MemoryItem[] = [
    {
        id: "m_role",
        category: "Context",
        text: "You oversee 42 locations across the Midwest region.",
        source: "From your org profile"
    },
    {
        id: "m_priority",
        category: "Preference",
        text: "Prioritize speed of service and loyalty growth in summaries.",
        source: "You told PAR Intelligence"
    },
    {
        id: "m_format",
        category: "Preference",
        text: "Prefer concise, bulleted answers with a clear recommended action.",
        source: "Learned from your feedback"
    },
    {
        id: "m_target",
        category: "Fact",
        text: "Drive-thru speed of service target is 180 seconds.",
        source: "From operations standards"
    },
    {
        id: "m_pinned",
        category: "Pinned",
        text: "Q3 focus: recover loyalty enrollment to 22% of transactions.",
        source: "Pinned by you"
    }
];

// ── Sample conversation (for history replay / seeded chat) ───────────────────
export const sampleConversation: ConversationMessage[] = [
    {
        id: "c_user_1",
        role: "user",
        text: "Why did loyalty signups dip this week?",
        timestamp: "8:03 AM"
    },
    {
        id: "c_ai_1",
        role: "assistant",
        text: "Loyalty enrollment fell 8.2% week-over-week, concentrated in 4 of your 42 locations. The dip lines up with shorter staffing at the register during the lunch rush, where most in-store sign-ups happen.",
        timestamp: "8:03 AM",
        sources: ["Loyalty · 94% confidence", "POS staffing"],
        metrics: [
            {label: "Enrollment", value: "-8.2%", direction: "down"},
            {label: "Lunch coverage", value: "-11%", direction: "down"},
            {label: "Affected stores", value: "4", direction: "flat"}
        ],
        followUps: [
            "Draft a recovery play for those 4 stores",
            "Which managers should I notify?",
            "Show enrollment by daypart"
        ],
        actions: [
            {
                id: "c_ai_1_winback",
                kind: "campaign",
                category: "Launch Campaign",
                title: "Launch a loyalty recovery campaign",
                description:
                    "Create a Punchh sign-up push for the 4 affected stores with a first-visit bonus to rebuild enrollment.",
                ctaLabel: "Launch Campaign",
                result: 'Drafted a "Double points on sign-up" campaign scoped to the 4 affected stores, scheduled to run the lunch daypart for 14 days. It is queued for your approval in Punchh.'
            },
            {
                id: "c_ai_1_staffing",
                kind: "schedule",
                category: "Adjust Staffing",
                title: "Add register coverage at lunch",
                description:
                    "Propose a labor tweak that adds one cashier during the 11am–1pm rush at the 4 stores driving the dip.",
                ctaLabel: "Adjust Schedule",
                result: "Built a proposed schedule change adding one register associate from 11:00am–1:00pm at the 4 stores. Projected to recover ~6% of in-store sign-ups. Sent to the GMs for confirmation."
            }
        ]
    }
];

const greetings = ["Here's what I found.", "On it.", "Got it — quick read:"];

/**
 * Deterministic canned assistant reply for the demo. Echoes intent and returns
 * plausible-looking metrics so the experience feels real without a backend.
 */
export function buildAssistantReply(
    prompt: string,
    index: number
): ConversationMessage {
    const greeting = greetings[index % greetings.length];
    return {
        id: `ai_${Date.now()}`,
        role: "assistant",
        text:
            `${greeting} Based on the last 30 days across your locations, ` +
            `here's the read on "${prompt.trim()}". Two locations account for most ` +
            "of the movement, and the trend is recoverable with a focused action this week.",
        timestamp: "Just now",
        sources: ["PAR Intelligence", "92% confidence"],
        metrics: [
            {label: "Impact", value: "+$14.2K", direction: "up"},
            {label: "Trend", value: "+3.1%", direction: "up"},
            {label: "Locations", value: "2 key", direction: "flat"}
        ],
        followUps: [
            "Draft an action plan",
            "Compare to last month",
            "Schedule this as an agent"
        ],
        actions: [
            {
                id: `act_${Date.now()}_plan`,
                kind: "automation",
                category: "Take Action",
                title: "Build a focused action plan",
                description:
                    "Turn this into a prioritized, assignable plan for the 2 key locations with owners and due dates.",
                ctaLabel: "Create Action Plan",
                result: "Created a 3-step action plan for the 2 key locations — each step has an owner, a due date, and a check-in after one period. It is ready in your task queue for review."
            },
            {
                id: `act_${Date.now()}_agent`,
                kind: "schedule",
                category: "Automate",
                title: "Schedule this as a recurring agent",
                description:
                    "Re-run this analysis automatically every morning and alert you when the trend moves past target.",
                ctaLabel: "Schedule Agent",
                result: "Scheduled a new agent to re-run this analysis daily at 6:00 AM and notify you when the trend crosses your target threshold. You can manage it from the Agents tab."
            }
        ]
    };
}
